import React from 'react';
import { RunRow } from './RunHistory';
import { Search, ScanSearch, CheckCircle2, XCircle, Loader2, RefreshCw, Building2, Zap } from 'lucide-react';

const QUICK_STAGES = {
  enriching: 'Pulling business details…',
  analyzing: 'Running deep analysis…',
  proposal: 'Writing the proposal…',
  done: 'Analysis + proposal ready',
};

export const JobsPanel = ({ theme, jobs, runs = [], api, workspaceId, onRefresh }) => {
  const { cardBg, cardBorder, textPrimary, textSecondary, inputBg } = theme;
  const rows = [...runs.map(r => ({ kind: 'run', at: r.created_at, r })),
    ...jobs.filter(j => j.type !== 'bulk_analyze' || (j.progress?.total || 0) > 0).map(j => ({ kind: 'job', at: j.created_at, j }))]
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));

  return (
    <div className={`rounded-2xl border ${cardBorder} ${cardBg}`} data-testid="jobs-panel">
      <div className={`flex items-center justify-between px-4 py-3 border-b ${cardBorder}`}>
        <p className={`text-sm font-semibold ${textPrimary}`}>History</p>
        <button onClick={onRefresh} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${cardBorder} ${inputBg} ${textPrimary}`}
          data-testid="jobs-refresh-btn">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <div className="divide-y divide-white/5">
        {rows.length === 0 && (
          <p className={`px-4 py-10 text-center text-sm ${textSecondary}`}>No jobs yet.</p>
        )}
        {rows.map(row => {
          if (row.kind === 'run') return <RunRow key={row.r.run_id} run={row.r} api={api} workspaceId={workspaceId} theme={theme} />;
          const j = row.j;
          const p = j.progress || {};
          const isSearch = j.type === 'search';
          const isSub = j.type === 'sub_account';
          const isQuick = j.type === 'quick_analyze';
          const pctW = isSearch
            ? (p.pct != null ? p.pct : Math.round(100 * (p.queries_done || 0) / Math.max(p.queries_total || 1, 1)))
            : Math.round(100 * (p.done || 0) / Math.max(p.total || 1, 1));
          return (
            <div key={j.job_id} className="px-4 py-3 flex items-center gap-3" data-testid={`job-row-${j.job_id}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                isSearch ? 'bg-teal-400/10 text-teal-400' : isSub ? 'bg-sky-400/10 text-sky-400' : isQuick ? 'bg-fuchsia-400/10 text-fuchsia-400' : 'bg-violet-400/10 text-violet-400'}`}>
                {isSearch ? <Search size={15} /> : isSub ? <Building2 size={15} /> : isQuick ? <Zap size={15} /> : <ScanSearch size={15} />}
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${textPrimary}`}>
                  {isSearch
                    ? `Search — ${(j.params?.industries || []).join(', ')} in ${j.params?.city}`
                    : isSub
                      ? `Sub-account setup — ${j.params?.sub_name || j.params?.lead_name}`
                      : isQuick
                        ? `Quick analyze & proposal — ${j.params?.domain || j.params?.website || ''}`
                        : `Bulk analysis — ${p.total || 0} leads`}
                </p>
                <p className={`text-[11px] ${textSecondary} mt-0.5`}>
                  {isSearch
                    ? `${p.new || 0} new leads (${p.found || 0} scanned${p.target ? ` · up to ${p.target}/industry` : ''}) · ${p.queries_done || 0}/${p.queries_total || 0} industries`
                    : isSub
                      ? (j.status === 'running' ? 'Creating sub-account & generating brand profile…'
                        : j.status === 'done' ? `Sub-account created${p.brand_profile_started ? ' · brand profile generating from their website' : ''}`
                          : 'Failed')
                      : isQuick
                        ? (QUICK_STAGES[p.stage] || (j.status === 'done' ? 'Analysis + proposal ready' : 'Working…'))
                        : `${p.done || 0}/${p.total || 0} analyzed${p.failed ? ` · ${p.failed} failed` : ''}`}
                  {j.error && <span className="text-amber-500"> · {j.error}</span>}
                </p>
                {j.status === 'running' && !isSub && !isQuick && (
                  <div className={`h-1 mt-2 rounded-full overflow-hidden ${inputBg}`}>
                    <div className="h-full bg-gradient-to-r from-teal-400 to-violet-400 rounded-full transition-all"
                      style={{ width: `${pctW}%` }} />
                  </div>
                )}
              </div>
              <div className="shrink-0">
                {j.status === 'running' && <Loader2 size={16} className="animate-spin text-teal-400" />}
                {j.status === 'done' && <CheckCircle2 size={16} className="text-emerald-400" />}
                {j.status === 'error' && <XCircle size={16} className="text-red-400" />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JobsPanel;
